import { joiResolver } from '@hookform/resolvers/joi';
import { Radio } from '@mui/material';


import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  MUIDataTable,
  MUIDataTableColumnDef,
  TextFieldForm,
  Typography,
} from 'web-library';
import { candidateService } from '../../../api';
import {
  ScDetailFromData,
  usePutSubject,
} from '../../../api-refactored/special-consideration/usePutSubject';
import {
  SpecialConsiderationSubjectModel,
  TypeAndReasonModel,
} from '../../../api/candidate/special-consideration/model';
import ControlledAutocomplete from '../../../components/form-items/ControlledAutocomplete';
import { stringToSemanticDisplay } from '../../../utils/DateUtils';
import { useRemainingCharacter } from '../../../hooks/useRemainingCharacter';
import AddReasonSchema from '../../../pages/groupSpecialConsideration/UploadRankingDetail/schema/AddReasonSchema';

const useStyles = makeStyles()((theme) =>
({
  radioButton: {
    padding: '0px',
    margin: '0px',
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  buttonContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  addIcon: {
    verticalAlign: 'sub',
    marginRight: 10,
    color: theme.color.icon.iconInverse,
  },
  selectSubjectText: {
    marginBottom: theme.spacing(3),
  },
  reasonTitle: {
    marginBottom: theme.spacing(3),
  },
  customSelect: {
    marginTop: theme.spacing(3),
  },
  divider: {
    marginTop: theme.spacing(5),
  },
}));

const getTableColumns = (t: any, subjectSelected: SpecialConsiderationSubjectModel) => {
  const columns: MUIDataTableColumnDef[] = [
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.ExamLevel'),
      name: 'examLevel',
      options: {
        setCellProps: () => ({ style: { width: '18%' } }),
      },
    },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.Exam Series'),
      name: 'examSeries',
    },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.SubjectPaper'),
      name: 'subject',
    },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.ModeOfAssessment'),
      name: 'modeOfAssessment',
    },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.Exam Date'),
      name: 'examDate',
      options: {
        setCellProps: () => ({ style: { width: '18%' } }),
        customBodyRenderLite: () =>
          stringToSemanticDisplay(
            subjectSelected && subjectSelected.examDate ? subjectSelected.examDate : ''
          ),
      },
    },
  ];
  return columns;
};

const CustomCheckbox = (props: {}) => {
  const newProps = { ...props };
  const { classes } = useStyles();
  return <Radio className={classes.radioButton} size="medium" {...newProps} />;
};

interface EditSubjectModalProps {
  modalState: boolean;
  onDialogClose: () => void;
  subjectSelected: SpecialConsiderationSubjectModel;
  refetchSubjectTable: () => void;
}

const EditSubjectModal = ({
  modalState,
  onDialogClose,
  subjectSelected,
  refetchSubjectTable,
}: EditSubjectModalProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { uuid: requestUuid } = useParams<{ uuid: string }>();
  const { specialConsiderationTypeCode: scTypeCode, reasonCode, explanation } = subjectSelected;
  const [selectedScType, setSelectedScType] = useState<string>(scTypeCode ?? '');

 const form = useForm<any>({
    mode: 'onSubmit',
    resolver: joiResolver(AddReasonSchema()),
    defaultValues: {
      explanation,
      specialConsiderationType: scTypeCode,
      reason: reasonCode,
    },
  });
  const { formState: { errors }, handleSubmit, control, reset, setValue } = form;
  useEffect(() => {
    reset({
      explanation,
      specialConsiderationType: scTypeCode,
      reason: reasonCode,
    });
    setSelectedScType(scTypeCode);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [subjectSelected]);

  const {
    data: typesAndReasonsData,
    isLoading: isLoadingDropdownData,
    error: isDropdownDataError,
    refetch: refetchDropdownData,
  } = candidateService.specialConsideration.candidate.useGetTypesAndReasons();

  const scTypesOption = () => typesAndReasonsData?.specialConsiderationTypes ?? [];
  const reasonOption = () =>
    typesAndReasonsData?.specialConsiderationTypeReasonMap[selectedScType] ?? [];

  const edtSubjectRequest = usePutSubject({
    onSuccess: () => {
      refetchSubjectTable();
      onDialogClose();
    },
  });

  const tableColumns = getTableColumns(t, subjectSelected);

  const onEditClick = (formData: ScDetailFromData) => {
    edtSubjectRequest.mutate({
      explanation: formData.explanation,
      paperUuid: subjectSelected.paperUuid,
      reason: formData.reason,
      requestUuid,
      specialConsiderationType: formData.specialConsiderationType,
      subjectUuid: subjectSelected.subjectUuid,
      uuid: subjectSelected.uuid,
    });
  };

  const maximumRemarksLength = 500;
  const { remainingCharMsg, charactersRemainingHandler } = useRemainingCharacter({
    maximumCharLength: maximumRemarksLength,
  });

  useEffect(() => {
    if (modalState) {
      charactersRemainingHandler(explanation.length);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [modalState]);

  return (
    <Dialog open={modalState} maxWidth="md" onClose={onDialogClose}>
      <DialogTitle>
        <Typography variant="h4">
          {t('specialConsideration->>ApplyForSpecialConsideration.EditSubjectPaper')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit((formData) => onEditClick(formData))} id="edit-form">
          <DialogContentText>
            {t('specialConsideration->>Dialog.SelectSubjectPaperInstruction')}
          </DialogContentText>
          <MUIDataTable
            columns={tableColumns}
            data={[subjectSelected]}
            options={{
              selectableRows: 'single',
              rowsSelected: [0],
            }}
            components={{
              Checkbox: CustomCheckbox,
            }}
          />
          <div className={classes.divider}>
            <Divider />
          </div>
          <Typography variant="h3" className={classes.selectSubjectText}>
            {subjectSelected.subject}
          </Typography>
          <Typography variant="h4" className={classes.reasonTitle}>
            {t('specialConsideration->>Dialog.SelectScTypeAndReason')}
          </Typography>
          <div>
            <ControlledAutocomplete<TypeAndReasonModel, false, false, undefined>
              label={t(
                'specialConsideration->>ApplyForSpecialConsideration.Special Consideration Type'
              )}
              placeholder={t(
                'specialConsideration->>ApplyForSpecialConsideration.SelectSpecialConsiderationType'
              )}
              control={control}
              name="specialConsiderationType"
              id="input-select-special-consideration-type"
              options={scTypesOption()}
              error={!!errors.specialConsiderationType}
              helperText={errors?.specialConsiderationType?.message?.toString()}
              getOptionLabel={(option: TypeAndReasonModel) => option.children}
              transform={{
                input: (selectedOption, options) =>
                  options.find((option) => option.value === selectedOption) ?? null,
                output: (selectedOption) => {
                  const selectValue = selectedOption ? selectedOption.value : null;
                  setSelectedScType(selectValue);
                  setValue('reason', '');
                  return selectValue;
                },
              }}
              isRequired
              loading={isLoadingDropdownData}
              loadingError={!!isDropdownDataError}
              loadingErrorOnReFetch={refetchDropdownData}
            />
          </div>
          <div className={classes.customSelect}>
            <ControlledAutocomplete<TypeAndReasonModel, false, false, undefined>
              label={t('specialConsideration->>ApplyForSpecialConsideration.Reason')}
              placeholder={t('specialConsideration->>ApplyForSpecialConsideration.SelectReason')}
              control={control}
              name="reason"
              id="input-select-special-consideration-reason"
              options={reasonOption()}
              error={!!errors.reason}
              helperText={errors?.reason?.message?.toString()}
              getOptionLabel={(option: TypeAndReasonModel) => option.children}
              transform={{
                input: (selectedOption, options) =>
                  options.find((option) => option.value === selectedOption) ?? null,
                output: (selectedOption) => (selectedOption ? selectedOption.value : null),
              }}
              isRequired
              loading={isLoadingDropdownData}
              loadingError={!!isDropdownDataError}
              loadingErrorOnReFetch={refetchDropdownData}
            />
          </div>
          <div className={classes.customSelect}>
            <TextFieldForm
              isRequired
              form={form}
              label={t('specialConsideration->>ApplyForSpecialConsideration.Remarks')}
              subLabel={t('specialConsideration->>ApplyForSpecialConsideration.RemarksSubLabel')}
              placeholder={t('specialConsideration->>ApplyForSpecialConsideration.EnterRemarks')}
              name="explanation"
              type="text"
              fullWidth
              multiline
              rows={4}
              error={!!errors.explanation}
              helperText={errors?.explanation ? errors?.explanation?.message?.toString() : remainingCharMsg}
              inputProps={{
                maxLength: maximumRemarksLength,
              }}
              onChange={(e) => {
                charactersRemainingHandler(e.target.value.length);
              }}
            />
          </div>
        </form>
      </DialogContent>
      <DialogActions id="candidate-dialog-action">
        <Box className={classes.buttonContainer}>
          <Button
            onClick={() => {
              edtSubjectRequest.reset();
              onDialogClose();
            }}
            variant="text"
          >
            {t('specialConsideration->>Dialog.Cancel')}
          </Button>
          <ApiButton status={edtSubjectRequest.status} type="submit" form="edit-form">
            {t('components->>IconLabel.SaveChanges')}
          </ApiButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditSubjectModal;
